import { OtherMetaData } from './../../models/dtos/Classes/OtherMetaData';
import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceCalculationResult, SellPriceConfig } from '../../models/sol-estimate-table/SellPriceConfig';
import { EscalationData as EscalationDataDTO, EstimateTab, EstimateGrossMargin, EstimateSellPrice, Escalations, SQWebProject, SQWebMaterialCategory } from '../../models/dtos';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { CommonDataService } from '../common-data.service';
import { ShippingHandlingData } from '../../models/sol-estimate-table/ShippingHandlingData';
import { WarrantyData } from '../../models/sol-estimate-table/WarrantyData';
import {
  EstimateUiModel,
  EstimateMaterialTypeModel,
  EstimateRowModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { MaterialSubCategory, SolMaterialEscalationType } from '../../models/enum';
import {
  EstimateLastModifiedSellPriceService,
  MaterialCategoryKey,
  MaterialSubRowKey,
} from './estimate-last-modified-sell-price.service';
import { roundHalfEven9 } from '../../../types/number-extensions';

/** Maps each material category and subrow to its stored sell-price and GM% fields. */
const MATERIAL_ROW_FIELDS: Record<MaterialCategoryKey, Record<MaterialSubRowKey, { sellPrice: keyof EstimateSellPrice; gmPercent: keyof EstimateGrossMargin }>> = {
  siemensMaterial: {
    headerRow: { sellPrice: 'siemensMaterialSellPrice', gmPercent: 'siemensMaterialGMPercent' },
    shippingAndHandling: { sellPrice: 'siemensMaterialSHSellPrice', gmPercent: 'siemensMaterialShippingHandlingGMPercent' },
    warranty: { sellPrice: 'siemensMaterialWarrantySellPrice', gmPercent: 'siemensMaterialWarrantyGMPercent' }
  },
  outsideMaterial: {
    headerRow: { sellPrice: 'outsideMaterialSellPrice', gmPercent: 'outsideMaterialGMPercent' },
    shippingAndHandling: { sellPrice: 'outsideMaterialSHSellPrice', gmPercent: 'outsideMaterialShippingHandlingGMPercent' },
    warranty: { sellPrice: 'outsideMaterialWarrantySellPrice', gmPercent: 'outsideMaterialWarrantyGMPercent' }
  },
  mechanicalMaterial: {
    headerRow: { sellPrice: 'mechMaterialSellPrice', gmPercent: 'mechanicalMaterialGMPercent' },
    shippingAndHandling: { sellPrice: 'mechMaterialSHSellPrice', gmPercent: 'mechanicalMaterialShippingHandlingGMPercent' },
    warranty: { sellPrice: 'mechMaterialWarrantySellPrice', gmPercent: 'mechanicalMaterialWarrantyGMPercent' }
  },
  electricalMaterial: {
    headerRow: { sellPrice: 'electMaterialSellPrice', gmPercent: 'electricalMaterialGMPercent' },
    shippingAndHandling: { sellPrice: 'elecMaterialSHSellPrice', gmPercent: 'electricalMaterialShippingHandlingGMPercent' },
    warranty: { sellPrice: 'elecMaterialWarrantySellPrice', gmPercent: 'electricalMaterialWarrantyGMPercent' }
  }
};

/**
 * Handles all material-costs-related logic for the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateMaterialService {

  /**
   * Stored reference to the EstimateUiModel, set by the orchestrator
   * during model initialisation so commit methods can access it.
   */
  private modelRef!: EstimateUiModel;

  constructor(
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly commonDataService: CommonDataService,
    private readonly lastModifiedSellPriceService: EstimateLastModifiedSellPriceService
  ) { }

  /**
   * Stores the model reference so that shipping/warranty commit
   * methods can patch it without receiving it as a parameter.
   * Called by the orchestrator after model creation / reset.
   */
  setModelReference(model: EstimateUiModel): void {
    this.modelRef = model;
  }

  // ── Patching (initial load) ────────────────────────────────────

  patchMaterialCosts(
    model: EstimateUiModel,
    config: SellPriceConfig,
    subTotalsCost: number,
    sqWebProject?: SQWebProject
  ): void {
    // Keep model reference in sync for commit methods
    this.modelRef = model;

    const gm: EstimateGrossMargin | undefined = this.commonDataService.getOtherMetaData()?.grossMargin;

    if (gm == null) {
      return;
    }

    // Extract cost, shipping & warranty from each SQWebMaterialCategory; GM%-based fallback for shipping/warranty
    const siemens = this.extractCategoryValues(sqWebProject.siemensMaterialCategory);
    const outside = this.extractCategoryValues(sqWebProject.outsideMaterialCategory);
    const mechanical = this.extractCategoryValues(sqWebProject.mechanicalMaterialCategory);
    const electrical = this.extractCategoryValues(sqWebProject.electricalMaterialCategory);

    // Get escalation rates for escalation enabled status
    const escalationRates: Escalations | undefined = this.commonDataService.getEstimateMetaData()?.escalationRates;

    this.patchMaterialType(
      model.materialCosts.siemensMaterial,
      siemens.cost, siemens.shippingCost, siemens.warrantyCost,
      gm.siemensMaterialGMPercent,
      gm.siemensMaterialShippingHandlingGMPercent,
      gm.siemensMaterialWarrantyGMPercent,
      escalationRates?.siemensMaterialEscalation,
    );

    this.patchMaterialType(
      model.materialCosts.outsideMaterial,
      outside.cost, outside.shippingCost, outside.warrantyCost,
      gm.outsideMaterialGMPercent,
      gm.outsideMaterialShippingHandlingGMPercent,
      gm.outsideMaterialWarrantyGMPercent,
      escalationRates?.outsideMaterialEscalation
    );

    this.patchMaterialType(
      model.materialCosts.mechanicalMaterial,
      mechanical.cost, mechanical.shippingCost, mechanical.warrantyCost,
      gm.mechanicalMaterialGMPercent,
      gm.mechanicalMaterialShippingHandlingGMPercent,
      gm.mechanicalMaterialWarrantyGMPercent,
      escalationRates?.mechanicalMaterialEscalation,
    );

    this.patchMaterialType(
      model.materialCosts.electricalMaterial,
      electrical.cost, electrical.shippingCost, electrical.warrantyCost,
      gm.electricalMaterialGMPercent,
      gm.electricalMaterialShippingHandlingGMPercent,
      gm.electricalMaterialWarrantyGMPercent,
      escalationRates?.electricalMaterialEscalation,
    );

    this.calculateMaterialSellPrices(model, config, subTotalsCost);
  }

  private patchMaterialType(
    materialType: EstimateMaterialTypeModel,
    baseCost: number,
    shippingAndHandlingCost: number,
    warrantyCost: number,
    headerGMPercent?: number,
    shippingGMPercent?: number,
    warrantyGMPercent?: number,
    escalationData?: EscalationDataDTO
  ): void {
    materialType.headerRow.cost = baseCost;
    materialType.shippingAndHandling.cost = shippingAndHandlingCost;
    materialType.warranty.cost = warrantyCost;
    materialType.headerRow.escalate = escalationData?.escalationEnabled ? CONSTANTS.Y : CONSTANTS.N;

    if (headerGMPercent != null) {
      materialType.headerRow.grossMarginPercentage = headerGMPercent;
    }
    if (shippingGMPercent != null) {
      materialType.shippingAndHandling.grossMarginPercentage = shippingGMPercent;
    }
    if (warrantyGMPercent != null) {
      materialType.warranty.grossMarginPercentage = warrantyGMPercent;
    }
  }

  /**
 * Extracts cost, shipping and warranty values from an SQWebMaterialCategory.
 * Falls back to GM%-based derivation for shipping/warranty when the category value is missing.
 */
  private extractCategoryValues(
    category: SQWebMaterialCategory | undefined,
  ): { cost: number; shippingCost: number; warrantyCost: number } {
    const cost = category.sumUpCost ?? CONSTANTS.ZERO;
    const shippingCost = category.shippingHandlingCost;
    const warrantyCost = category.warrantyCost;
    return { cost, shippingCost, warrantyCost };
  }

  // ── Recalculation ──────────────────────────────────────────────

  /**
   * Recalculates shipping & warranty costs for a specific material category
   * based on the current header row cost (which may be escalated).
   * Uses the shipping/warranty rates from EstimateGrossMargin.
   * @param materialType - The material type model to update
   * @param category - Which material category ('siemens' | 'outside' | 'mechanical' | 'electrical')
   */
  recalculateShippingAndWarrantyForCategory(
    materialType: EstimateMaterialTypeModel,
    category: MaterialSubCategory
  ): void {
    const gm: EstimateGrossMargin | undefined = this.commonDataService.getOtherMetaData()?.grossMargin;
    if (!gm) { return; }

    const materialCost = this.getProductPromotionAdjustedCostForMaterialSubCategory(materialType.headerRow.cost, category);
    materialType.shippingAndHandling.cost = this.estimateCommonFunctions.calculateShippingCostForCategory(materialCost, gm, category);
    materialType.warranty.cost = this.estimateCommonFunctions.calculateWarrantyCostForCategory(materialCost, gm, category);
  }

  calculateMaterialSellPrices(
    model: EstimateUiModel,
    config: SellPriceConfig,
    subTotalsCost?: number
  ): void {
    const stc = subTotalsCost ?? model.subTotals.cost;
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    const siemens = this.calculateSellPricesForCategory(model.materialCosts.siemensMaterial, config, stc, 'siemensMaterial');
    const outside = this.calculateSellPricesForCategory(model.materialCosts.outsideMaterial, config, stc, 'outsideMaterial');
    const mechanical = this.calculateSellPricesForCategory(model.materialCosts.mechanicalMaterial, config, stc, 'mechanicalMaterial');
    const electrical = this.calculateSellPricesForCategory(model.materialCosts.electricalMaterial, config, stc, 'electricalMaterial');

    totalCost += siemens.totalCost + outside.totalCost + mechanical.totalCost + electrical.totalCost;
    totalSellPrice += siemens.totalSellPrice + outside.totalSellPrice + mechanical.totalSellPrice + electrical.totalSellPrice;

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.materialCosts.materialCostsHeader,
      totalCost, totalSellPrice, stc, config
    );
  }

  private calculateSellPricesForCategory(
    materialType: EstimateMaterialTypeModel,
    config: SellPriceConfig,
    stc: number,
    category: MaterialCategoryKey
  ): { totalCost: number; totalSellPrice: number } {
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    const shippingResult = this.calculateAndUpdateRow(materialType.shippingAndHandling, config, stc, category, 'shippingAndHandling');
    totalCost += shippingResult.cost;
    totalSellPrice += shippingResult.sellPrice;

    const warrantyResult = this.calculateAndUpdateRow(materialType.warranty, config, stc, category, 'warranty');
    totalCost += warrantyResult.cost;
    totalSellPrice += warrantyResult.sellPrice;

    const headerResult = this.calculateAndUpdateRow(materialType.headerRow, config, stc, category, 'headerRow');
    totalCost += headerResult.cost;
    totalSellPrice += headerResult.sellPrice;

    return { totalCost, totalSellPrice };
  }

  // ── Totals Recalculation ───────────────────────────────────────

  private calculateAndUpdateRow(
    row: EstimateRowModel,
    config: SellPriceConfig,
    stc: number,
    category: MaterialCategoryKey,
    subRow: MaterialSubRowKey
  ): { cost: number; sellPrice: number } {
    const result = config.isFixedPriceSelected !== null
      ? this.computeFixedPriceResult(row, config, category, subRow)
      : this.computeLineItemResult(row, config, category, subRow);
    this.estimateCommonFunctions.updateRowResult(row, result, stc);
    return { cost: row.cost, sellPrice: result.sellPrice };
  }

  private computeFixedPriceResult(
    row: EstimateRowModel,
    config: SellPriceConfig,
    category: MaterialCategoryKey,
    subRow: MaterialSubRowKey
  ): SellPriceCalculationResult {
    const presentPercent = row.grossMarginPercentage;
    const gmPercent = this.estimateCommonFunctions.resolveRowGMPercent(
      config, Number.isNaN(presentPercent) ? undefined : presentPercent
    );
    const preservedSellPrice = this.estimateCommonFunctions.resolvePreservedSellPrice(
      row, config, this.lastModifiedSellPriceService.isMaterialSellPriceLastModified(category, subRow)
    );
    return this.estimateCommonFunctions.calculateRowSellPrice(
      row.cost, gmPercent, config.isMarkUpSelected, preservedSellPrice
    );
  }

  private computeLineItemResult(
    row: EstimateRowModel,
    config: SellPriceConfig,
    category: MaterialCategoryKey,
    subRow: MaterialSubRowKey
  ): SellPriceCalculationResult {
    if (this.lastModifiedSellPriceService.isMaterialSellPriceLastModified(category, subRow)) {
      const storedSellPrice = this.getMaterialSellPrice(category, subRow);
      return this.estimateCommonFunctions.recomputePercentsFromSellPrice(row.cost, storedSellPrice);
    }
    const gmPercent = this.getMaterialGMPercent(category, subRow);
    return this.estimateCommonFunctions.calculateRowSellPrice(row.cost, gmPercent, config.isMarkUpSelected);
  }


  /** Returns the stored sell price for a given material (category, subRow) from OtherMetaData.sellPrice. */
  getMaterialSellPrice(category: MaterialCategoryKey, subRow: MaterialSubRowKey): number {
    const sellPrices = this.commonDataService.getOtherMetaData()?.sellPrice;
    if (!sellPrices) { return CONSTANTS.ZERO; }
    return sellPrices[MATERIAL_ROW_FIELDS[category][subRow].sellPrice] ?? CONSTANTS.ZERO;
  }

  /** Returns the stored GM% for a given material (category, subRow) from OtherMetaData.grossMargin. */
  getMaterialGMPercent(category: MaterialCategoryKey, subRow: MaterialSubRowKey): number {
    const grossMargin = this.commonDataService.getOtherMetaData()?.grossMargin;
    if (!grossMargin) { return CONSTANTS.ZERO; }
    return (grossMargin[MATERIAL_ROW_FIELDS[category][subRow].gmPercent] as number) ?? CONSTANTS.ZERO;
  }


  recalculateMaterialTotals(model: EstimateUiModel, config: SellPriceConfig): void {
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    const siemens = this.caculateTotalsForCategory(model.materialCosts.siemensMaterial);
    const outside = this.caculateTotalsForCategory(model.materialCosts.outsideMaterial);
    const mechanical = this.caculateTotalsForCategory(model.materialCosts.mechanicalMaterial);
    const electrical = this.caculateTotalsForCategory(model.materialCosts.electricalMaterial);

    totalCost += siemens.totalCost + outside.totalCost + mechanical.totalCost + electrical.totalCost;
    totalSellPrice += siemens.totalSellPrice + outside.totalSellPrice + mechanical.totalSellPrice + electrical.totalSellPrice;

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.materialCosts.materialCostsHeader,
      totalCost, totalSellPrice, model.subTotals.cost, config
    );
  }

  private caculateTotalsForCategory(materialType: EstimateMaterialTypeModel): { totalCost: number, totalSellPrice: number } {
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    totalCost += materialType.headerRow.cost;
    totalCost += materialType.shippingAndHandling.cost;
    totalCost += materialType.warranty.cost;

    totalSellPrice += materialType.headerRow.sellPrice;
    totalSellPrice += materialType.shippingAndHandling.sellPrice;
    totalSellPrice += materialType.warranty.sellPrice;

    return { totalCost, totalSellPrice };
  }

  // ── Reactive Handler ───────────────────────────────────────────

  onMaterialRowPercentChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    category: 'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial',
    subRow: 'headerRow' | 'shippingAndHandling' | 'warranty',
    newPercent: number,
    isGMPercent: boolean
  ): void {
    const isMarkUp = !isGMPercent;
    const row = model.materialCosts[category][subRow];
    const cost = row.cost;

    const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, newPercent, isMarkUp);

    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateMaterialTotals(model, config);
  }

  /**
   * Called when user edits a material row's sell price directly (Line Item mode only).
   * Derives GM%, MU%, and TDC% from the new sell price and cost, then recalculates totals.
   * This is the inverse of onMaterialRowPercentChanged — user edits sell price instead of GM%.
   *
   * Uses updateRowResult() to ensure all fields are calculated consistently:
   *  • sellPrice (user input)
   *  • grossMargin (sellPrice - cost)
   *  • grossMarginPercentage ((sellPrice - cost) / sellPrice * 100)
   *  • markUpPercentage ((sellPrice - cost) / cost * 100)
   *  • tdcPercentage (cost / subTotalsCost * 100)
   */
  onMaterialRowSellPriceChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    category: 'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial',
    subRow: 'headerRow' | 'shippingAndHandling' | 'warranty',
    newSellPrice: number
  ): void {
    const row = model.materialCosts[category][subRow];
    const cost = row.cost;

    // Derive GM%, MU%, and gmDollars from the new sell price
    const result = this.estimateCommonFunctions.recomputePercentsFromSellPrice(cost, newSellPrice);

    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateMaterialTotals(model, config);
  }

  // ── Refresh Displayed Percentages (GM↔MU toggle) ──────────────

  refreshMaterialPercentages(model: EstimateUiModel): void {
    this.refreshMaterialTypePercentsFromSellPrice(model.materialCosts.siemensMaterial);
    this.refreshMaterialTypePercentsFromSellPrice(model.materialCosts.outsideMaterial);
    this.refreshMaterialTypePercentsFromSellPrice(model.materialCosts.mechanicalMaterial);
    this.refreshMaterialTypePercentsFromSellPrice(model.materialCosts.electricalMaterial);
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.materialCosts.materialCostsHeader);
  }

  refreshMaterialTypePercentsFromSellPrice(materialType: EstimateMaterialTypeModel): void {
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(materialType.shippingAndHandling);
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(materialType.warranty);
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(materialType.headerRow);
  }

  // ── Shipping & Handling ────────────────────────────────────────

  getShippingData(): ShippingHandlingData[] {
    const estimateTabData = this.commonDataService.getEstimateMetaData()?.estimateTab;
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const grossMarginData = otherMetaData?.grossMargin;

    let shippingData: ShippingHandlingData[];

    if (this.commonDataService.globalPPMmode) {
      const ppmData = otherMetaData?.pricePresentationData;
      const sellPriceData = otherMetaData?.sellPrice;
      if (!estimateTabData || !grossMarginData || !ppmData || !sellPriceData) {
        console.error('Required project metadata not available for shipping data');
        return this.getDefaultShippingData();
      }

      shippingData = [
        {
          shipping: CONSTANTS.SIEMENS_MATERIAL,
          dollars: ppmData.siemensDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ?
            sellPriceData.siemensMaterialSellPrice :
            this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Siemens_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.siemensMaterialShipping,
          total: CONSTANTS.ZERO
        },
        {
          shipping: CONSTANTS.OUTSIDE_MATERIAL,
          dollars: ppmData.outsideDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ?
            sellPriceData.outsideMaterialSellPrice :
            this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Outside_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.outsideMaterialShipping,
          total: CONSTANTS.ZERO
        },
        {
          shipping: CONSTANTS.MECHANICAL_MATERIAL,
          dollars: ppmData.mechanicalDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ?
            sellPriceData.mechMaterialSellPrice :
            this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Mechanical_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.machanicalMaterialShipping,
          total: CONSTANTS.ZERO
        },
        {
          shipping: CONSTANTS.ELECTRICAL_MATERIAL,
          dollars: ppmData.electricalDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ?
            sellPriceData.electMaterialSellPrice :
            this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Electrical_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.electricalMaterialShipping,
          total: CONSTANTS.ZERO
        },
      ];
    }
    else {

      if (!estimateTabData || !grossMarginData) {
        console.error('Required project metadata not available for shipping data');
        return this.getDefaultShippingData();
      }

      shippingData = [
        {
          shipping: CONSTANTS.SIEMENS_MATERIAL,
          dollars: this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Siemens_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.siemensMaterialShipping,
          total: CONSTANTS.ZERO
        },
        {
          shipping: CONSTANTS.OUTSIDE_MATERIAL,
          dollars: this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Outside_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.outsideMaterialShipping,
          total: CONSTANTS.ZERO
        },
        {
          shipping: CONSTANTS.MECHANICAL_MATERIAL,
          dollars: this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Mechanical_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.machanicalMaterialShipping,
          total: CONSTANTS.ZERO
        },
        {
          shipping: CONSTANTS.ELECTRICAL_MATERIAL,
          dollars: this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Electrical_Material) ?? CONSTANTS.ZERO,
          rate: grossMarginData.electricalMaterialShipping,
          total: CONSTANTS.ZERO
        },
      ];
    }

    return shippingData;
  }

  private getDefaultShippingData(): ShippingHandlingData[] {
    return [
      { shipping: CONSTANTS.SIEMENS_MATERIAL, dollars: CONSTANTS.ZERO, rate: CONSTANTS.ZERO, total: CONSTANTS.ZERO },
      { shipping: CONSTANTS.OUTSIDE_MATERIAL, dollars: CONSTANTS.ZERO, rate: CONSTANTS.ZERO, total: CONSTANTS.ZERO },
      { shipping: CONSTANTS.MECHANICAL_MATERIAL, dollars: CONSTANTS.ZERO, rate: CONSTANTS.ZERO, total: CONSTANTS.ZERO },
      { shipping: CONSTANTS.ELECTRICAL_MATERIAL, dollars: CONSTANTS.ZERO, rate: CONSTANTS.ZERO, total: CONSTANTS.ZERO }
    ];
  }

  calculateShippingCost(dollars: number, rate: number, category: SolMaterialEscalationType): number {
    const materialCost = this.getProductPromotionAdjustedCost(dollars, category);
    return this.estimateCommonFunctions.calculateShippingCost(materialCost, rate);
  }

  getProductPromotionAdjustedCost(cost: number, category: SolMaterialEscalationType): number {
    return this.estimateCommonFunctions.getShippingWarrantyBaseCost(cost, category);
  }

  private getEscalatedActualMaterialCost(estimateTab: EstimateTab | undefined, category: SolMaterialEscalationType): number | undefined {
    return this.estimateCommonFunctions.getEscalatedActualMaterialCost(estimateTab, category);
  }

  private getProductPromotionAdjustedCostForMaterialSubCategory(cost: number, category: MaterialSubCategory): number {
    switch (category) {
      case MaterialSubCategory.Siemens:
        return this.getProductPromotionAdjustedCost(cost, SolMaterialEscalationType.Siemens_Material);
      case MaterialSubCategory.Outside:
        return this.getProductPromotionAdjustedCost(cost, SolMaterialEscalationType.Outside_Material);
      case MaterialSubCategory.Mechanical:
        return this.getProductPromotionAdjustedCost(cost, SolMaterialEscalationType.Mechanical_Material);
      case MaterialSubCategory.Electrical:
        return this.getProductPromotionAdjustedCost(cost, SolMaterialEscalationType.Electrical_Material);
      default:
        return cost;
    }
  }

  calculateShippingRateFromTotal(total: number, dollars: number): number {
    return this.estimateCommonFunctions.calculateShippingRateFromTotal(total, dollars);
  }

  /**
   * Patches estimated shipping costs into the model.
   * Updates each material category's shippingAndHandling.cost from the dialog data.
   */
  patchShippingData(updatedShippingData: ShippingHandlingData[]): void {
    if (updatedShippingData.length < CONSTANTS.FOUR) { return; }
    this.modelRef.materialCosts.siemensMaterial.shippingAndHandling.cost = updatedShippingData[CONSTANTS.ZERO].total;
    this.modelRef.materialCosts.outsideMaterial.shippingAndHandling.cost = updatedShippingData[CONSTANTS.ONE].total;
    this.modelRef.materialCosts.mechanicalMaterial.shippingAndHandling.cost = updatedShippingData[CONSTANTS.TWO].total;
    this.modelRef.materialCosts.electricalMaterial.shippingAndHandling.cost = updatedShippingData[CONSTANTS.THREE].total;
  }

  /**
   * Writes shipping rates back to the grossMargin DTO.
   */
  updateShippingDataToDTO(updatedShippingData: ShippingHandlingData[]): void {
    if (updatedShippingData.length < CONSTANTS.FOUR) { return; }
    const grossMarginData = this.commonDataService.getOtherMetaData()?.grossMargin;
    if (!grossMarginData) { return; }
    grossMarginData.siemensMaterialShipping = updatedShippingData[CONSTANTS.ZERO].rate;
    grossMarginData.outsideMaterialShipping = updatedShippingData[CONSTANTS.ONE].rate;
    grossMarginData.machanicalMaterialShipping = updatedShippingData[CONSTANTS.TWO].rate;
    grossMarginData.electricalMaterialShipping = updatedShippingData[CONSTANTS.THREE].rate;
  }

  // ── Warranty ───────────────────────────────────────────────────

  /**
   * Builds the warranty dialog input data from project metadata.
   * Base cost follows the same PPM/standard rule as shipping:
   *   PPM + discntType === 1  → sell price
   *   all other cases         → actual (non-discounted) material cost
   *
   * Rates come from EstimateGrossMargin (all 5 years per category).
   */
  getWarrantyData(): WarrantyData[] {
    const estimateTabData = this.commonDataService.getEstimateMetaData()?.estimateTab;
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const grossMarginData = otherMetaData?.grossMargin;

    if (!estimateTabData || !grossMarginData) {
      console.error('Required project metadata not available for warranty data');
      return this.getDefaultWarrantyData();
    }

    let baseSiemens: number;
    let baseOutside: number;
    let baseMechanical: number;
    let baseElectrical: number;

    if (this.commonDataService.globalPPMmode) {
      const ppmData = otherMetaData?.pricePresentationData;
      const sellPriceData = otherMetaData?.sellPrice;

      if (!ppmData || !sellPriceData) {
        console.error('Required PPM metadata not available for warranty data');
        return this.getDefaultWarrantyData();
      }

      baseSiemens = ppmData.siemensDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ? sellPriceData.siemensMaterialSellPrice : this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Siemens_Material) ?? CONSTANTS.ZERO;
      baseOutside = ppmData.outsideDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ? sellPriceData.outsideMaterialSellPrice : this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Outside_Material) ?? CONSTANTS.ZERO;
      baseMechanical = ppmData.mechanicalDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ? sellPriceData.mechMaterialSellPrice : this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Mechanical_Material) ?? CONSTANTS.ZERO;
      baseElectrical = ppmData.electricalDiscntType === CONSTANTS.PPM_DISCOUNT_TYPE_DISCOUNT ? sellPriceData.electMaterialSellPrice : this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Electrical_Material) ?? CONSTANTS.ZERO;
    } else {
      baseSiemens = this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Siemens_Material) ?? CONSTANTS.ZERO;
      baseOutside = this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Outside_Material) ?? CONSTANTS.ZERO;
      baseMechanical = this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Mechanical_Material) ?? CONSTANTS.ZERO;
      baseElectrical = this.getEscalatedActualMaterialCost(estimateTabData, SolMaterialEscalationType.Electrical_Material) ?? CONSTANTS.ZERO;
    }

    return [
      {
        warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_SIEMENS,
        dollars: baseSiemens,
        year1Rate: grossMarginData.siemensMaterialWarranty,
        year2Rate: grossMarginData.siemensMaterialWarrantyForSecondYear,
        year3Rate: grossMarginData.siemensMaterialWarrantyForThirdYear,
        year4Rate: grossMarginData.siemensMaterialWarrantyForFourthYear,
        year5Rate: grossMarginData.siemensMaterialWarrantyForFifthYear,
        total: this.calculateWarrantyTotal(baseSiemens, grossMarginData.siemensMaterialWarranty, grossMarginData.siemensMaterialWarrantyForSecondYear, grossMarginData.siemensMaterialWarrantyForThirdYear, grossMarginData.siemensMaterialWarrantyForFourthYear, grossMarginData.siemensMaterialWarrantyForFifthYear, SolMaterialEscalationType.Siemens_Material)
      },
      {
        warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_OUTSIDE,
        dollars: baseOutside,
        year1Rate: grossMarginData.outsideMaterialWarranty,
        year2Rate: grossMarginData.outsideMaterialWarrantyForSecondYear,
        year3Rate: grossMarginData.outsideMaterialWarrantyForThirdYear,
        year4Rate: grossMarginData.outsideMaterialWarrantyForFourthYear,
        year5Rate: grossMarginData.outsideMaterialWarrantyForFifthYear,
        total: this.calculateWarrantyTotal(baseOutside, grossMarginData.outsideMaterialWarranty, grossMarginData.outsideMaterialWarrantyForSecondYear, grossMarginData.outsideMaterialWarrantyForThirdYear, grossMarginData.outsideMaterialWarrantyForFourthYear, grossMarginData.outsideMaterialWarrantyForFifthYear, SolMaterialEscalationType.Outside_Material)
      },
      {
        warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_MECHANICAL,
        dollars: baseMechanical,
        // note: EstimateGrossMargin uses 'machanical' typo for year-1 property
        year1Rate: grossMarginData.machanicalMaterialWarranty,
        year2Rate: grossMarginData.mechanicalMaterialWarrantyForSecondYear,
        year3Rate: grossMarginData.mechanicalMaterialWarrantyForThirdYear,
        year4Rate: grossMarginData.mechanicalMaterialWarrantyForFourthYear,
        year5Rate: grossMarginData.mechanicalMaterialWarrantyForFifthYear,
        total: this.calculateWarrantyTotal(baseMechanical, grossMarginData.machanicalMaterialWarranty, grossMarginData.mechanicalMaterialWarrantyForSecondYear, grossMarginData.mechanicalMaterialWarrantyForThirdYear, grossMarginData.mechanicalMaterialWarrantyForFourthYear, grossMarginData.mechanicalMaterialWarrantyForFifthYear, SolMaterialEscalationType.Mechanical_Material)
      },
      {
        warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_ELECTRICAL,
        dollars: baseElectrical,
        year1Rate: grossMarginData.electricalMaterialWarranty,
        year2Rate: grossMarginData.electricalMaterialWarrantyForSecondYear,
        year3Rate: grossMarginData.electricalMaterialWarrantyForThirdYear,
        year4Rate: grossMarginData.electricalMaterialWarrantyForFourthYear,
        year5Rate: grossMarginData.electricalMaterialWarrantyForFifthYear,
        total: this.calculateWarrantyTotal(baseElectrical, grossMarginData.electricalMaterialWarranty, grossMarginData.electricalMaterialWarrantyForSecondYear, grossMarginData.electricalMaterialWarrantyForThirdYear, grossMarginData.electricalMaterialWarrantyForFourthYear, grossMarginData.electricalMaterialWarrantyForFifthYear, SolMaterialEscalationType.Electrical_Material)
      }
    ];
  }

  getWarrantyLocked(): boolean {
    return this.commonDataService.getOtherMetaData()?.grossMargin?.warrantyLocked ?? false;
  }

  /**
   * TODO: Wire up to the actual project date once jobEndDate is exposed in a DTO.
   * Currently returns null; the dialog handles a missing date gracefully.
   */
  getJobEndDate(): Date | null {
    return this.commonDataService.getEstimateData()?.endDate ?? null;
  }

  /**
   * TODO: Wire up to the actual project date once jobStartDate is exposed in a DTO.
   * Currently returns null; the dialog handles a missing date gracefully.
   */
  getJobStartDate(): Date | null {
    return this.commonDataService.getEstimateData()?.startDate ?? null;
  }

  calculateWarrantyTotal(
    dollars: number,
    year1Rate: number,
    year2Rate: number,
    year3Rate: number,
    year4Rate: number,
    year5Rate: number,
    category: SolMaterialEscalationType
  ): number {
    const materialCost = this.getProductPromotionAdjustedCost(dollars, category);
    return this.estimateCommonFunctions.calculateWarrantyTotal(materialCost, year1Rate, year2Rate, year3Rate, year4Rate, year5Rate);
  }

  calculateWarrantyYear1RateFromTotal(total: number, dollars: number): number {
    return this.estimateCommonFunctions.calculateWarrantyYear1RateFromTotal(total, dollars);
  }

  /**
   * Patches estimated warranty costs into the model.
   * Updates each material category's warranty.cost from the dialog data.
   */
  patchWarrantyData(updatedWarrantyData: WarrantyData[]): void {
    if (updatedWarrantyData.length < CONSTANTS.FOUR) { return; }
    this.modelRef.materialCosts.siemensMaterial.warranty.cost = updatedWarrantyData[CONSTANTS.ZERO].total;
    this.modelRef.materialCosts.outsideMaterial.warranty.cost = updatedWarrantyData[CONSTANTS.ONE].total;
    this.modelRef.materialCosts.mechanicalMaterial.warranty.cost = updatedWarrantyData[CONSTANTS.TWO].total;
    this.modelRef.materialCosts.electricalMaterial.warranty.cost = updatedWarrantyData[CONSTANTS.THREE].total;
  }

  /**
   * Writes all warranty rates and the lock flag back to the grossMargin DTO.
   */
  updateWarrantyDataToDTO(updatedWarrantyData: WarrantyData[], locked: boolean): void {
    if (updatedWarrantyData.length < CONSTANTS.FOUR) { return; }
    const grossMarginData = this.commonDataService.getOtherMetaData()?.grossMargin;
    if (!grossMarginData) {
      return;
    }

    const siemens = updatedWarrantyData[CONSTANTS.ZERO];
    const outside = updatedWarrantyData[CONSTANTS.ONE];
    const mechanical = updatedWarrantyData[CONSTANTS.TWO];
    const electrical = updatedWarrantyData[CONSTANTS.THREE];

    grossMarginData.siemensMaterialWarranty = siemens.year1Rate;
    grossMarginData.siemensMaterialWarrantyForSecondYear = siemens.year2Rate;
    grossMarginData.siemensMaterialWarrantyForThirdYear = siemens.year3Rate;
    grossMarginData.siemensMaterialWarrantyForFourthYear = siemens.year4Rate;
    grossMarginData.siemensMaterialWarrantyForFifthYear = siemens.year5Rate;

    grossMarginData.outsideMaterialWarranty = outside.year1Rate;
    grossMarginData.outsideMaterialWarrantyForSecondYear = outside.year2Rate;
    grossMarginData.outsideMaterialWarrantyForThirdYear = outside.year3Rate;
    grossMarginData.outsideMaterialWarrantyForFourthYear = outside.year4Rate;
    grossMarginData.outsideMaterialWarrantyForFifthYear = outside.year5Rate;

    // note: DTO uses 'machanical' typo for year-1 property
    grossMarginData.machanicalMaterialWarranty = mechanical.year1Rate;
    grossMarginData.mechanicalMaterialWarrantyForSecondYear = mechanical.year2Rate;
    grossMarginData.mechanicalMaterialWarrantyForThirdYear = mechanical.year3Rate;
    grossMarginData.mechanicalMaterialWarrantyForFourthYear = mechanical.year4Rate;
    grossMarginData.mechanicalMaterialWarrantyForFifthYear = mechanical.year5Rate;

    grossMarginData.electricalMaterialWarranty = electrical.year1Rate;
    grossMarginData.electricalMaterialWarrantyForSecondYear = electrical.year2Rate;
    grossMarginData.electricalMaterialWarrantyForThirdYear = electrical.year3Rate;
    grossMarginData.electricalMaterialWarrantyForFourthYear = electrical.year4Rate;
    grossMarginData.electricalMaterialWarrantyForFifthYear = electrical.year5Rate;

    grossMarginData.warrantyLocked = locked;
  }

  private getDefaultWarrantyData(): WarrantyData[] {
    const zero = CONSTANTS.ZERO;
    return [
      { warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_SIEMENS, dollars: zero, year1Rate: zero, year2Rate: zero, year3Rate: zero, year4Rate: zero, year5Rate: zero, total: zero },
      { warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_OUTSIDE, dollars: zero, year1Rate: zero, year2Rate: zero, year3Rate: zero, year4Rate: zero, year5Rate: zero, total: zero },
      { warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_MECHANICAL, dollars: zero, year1Rate: zero, year2Rate: zero, year3Rate: zero, year4Rate: zero, year5Rate: zero, total: zero },
      { warrantyPeriodName: CONSTANTS.WARRANTY_PERIOD_ELECTRICAL, dollars: zero, year1Rate: zero, year2Rate: zero, year3Rate: zero, year4Rate: zero, year5Rate: zero, total: zero }
    ];
  }
}
